import adserver from 'services/adserver';
import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  QueryClient,
} from '@tanstack/react-query';
import { proposalsKeys } from './proposals.keys';
import {
  MaterialFileParams,
  MaterialLinkParams,
  MaterialTestParams,
} from './proposals.types';

const invalidateMaterials = (queryClient: QueryClient, proposalId: number) =>
  queryClient.invalidateQueries({
    queryKey: proposalsKeys.materials(proposalId),
  });

// files

const createMaterialFile = async ({
  proposalId,
  file,
  commentary,
  isEmissionCodesFile,
}: {
  proposalId: number;
  file: File;
  commentary: string;
  isEmissionCodesFile: boolean;
}): Promise<MaterialFileParams> => {
  const formData = new FormData();
  formData.append('uploadedFile', file);
  formData.append('name', file.name);
  formData.append('commentary', commentary);
  formData.append('isEmissionCodesFile', String(isEmissionCodesFile));

  const { data } = await adserver({
    url: `/proposal/${proposalId}/materials/file/`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const useCreateMaterialFile = (): UseMutationResult<
  MaterialFileParams,
  unknown,
  {
    proposalId: number;
    file: File;
    commentary: string;
    isEmissionCodesFile: boolean;
  },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMaterialFile,
    onSuccess: (_data, { proposalId }) =>
      invalidateMaterials(queryClient, proposalId),
  });
};

const deleteMaterialFile = async ({
  proposalId,
  fileId,
}: {
  proposalId: number;
  fileId: string | number;
}): Promise<void> => {
  await adserver({
    url: `/proposal/${proposalId}/materials/file/${fileId}`,
    method: 'DELETE',
    headers: {},
  });
};

export const useDeleteMaterialFile = (): UseMutationResult<
  void,
  unknown,
  { proposalId: number; fileId: string | number },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMaterialFile,
    onSuccess: (_data, { proposalId }) =>
      invalidateMaterials(queryClient, proposalId),
  });
};

const updateMaterialFileLabel = async ({
  proposalId,
  fileId,
  label,
}: {
  proposalId: number;
  fileId: string | number;
  label: string;
}): Promise<MaterialFileParams> => {
  const { data: response } = await adserver({
    url: `/proposal/${proposalId}/materials/file/${fileId}`,
    method: 'PATCH',
    data: { label },
    headers: {},
  });
  return response;
};

export const useUpdateMaterialFileLabel = (): UseMutationResult<
  MaterialFileParams,
  unknown,
  { proposalId: number; fileId: string | number; label: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMaterialFileLabel,
    onSuccess: (_response, { proposalId }) =>
      invalidateMaterials(queryClient, proposalId),
  });
};

// links

const createMaterialLink = async ({
  proposalId,
  url,
  commentary,
}: {
  proposalId: number;
  url: string;
  commentary: string;
}): Promise<MaterialLinkParams> => {
  const { data } = await adserver({
    url: `/proposal/${proposalId}/materials/link/`,
    method: 'POST',
    data: { url, commentary },
    headers: {},
  });
  return data;
};

export const useCreateMaterialLink = (): UseMutationResult<
  MaterialLinkParams,
  unknown,
  { proposalId: number; commentary: string; url: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMaterialLink,
    onSuccess: (_data, { proposalId }) =>
      invalidateMaterials(queryClient, proposalId),
  });
};

const deleteMaterialLink = async ({
  proposalId,
  linkId,
}: {
  proposalId: number;
  linkId: string | number;
}): Promise<void> => {
  await adserver({
    url: `/proposal/${proposalId}/materials/link/${linkId}`,
    method: 'DELETE',
    headers: {},
  });
};

export const useDeleteMaterialLink = (): UseMutationResult<
  void,
  unknown,
  { proposalId: number; linkId: string | number },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMaterialLink,
    onSuccess: (_data, { proposalId }) =>
      invalidateMaterials(queryClient, proposalId),
  });
};

// tests

const createMaterialTest = async ({
  proposalId,
  data,
  file,
}: {
  proposalId: number;
  data: string;
  file?: File;
}): Promise<MaterialTestParams> => {
  const formData = new FormData();
  formData.append('data', data);
  if (file) {
    formData.append('dataFile', file);
  }

  const { data: response } = await adserver({
    url: `/proposal/${proposalId}/materials/test/`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response;
};

export const useCreateMaterialTest = (): UseMutationResult<
  MaterialTestParams,
  unknown,
  { proposalId: number; data: string; file?: File },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMaterialTest,
    onSuccess: (_response, { proposalId }) =>
      invalidateMaterials(queryClient, proposalId),
  });
};

const updateMaterialTest = async ({
  proposalId,
  testId,
  data,
  file,
}: {
  proposalId: number;
  testId: string | number;
  data: string;
  file?: File;
}): Promise<MaterialTestParams> => {
  const formData = new FormData();
  formData.append('data', data);
  if (file) {
    formData.append('dataFile', file);
  }

  const { data: response } = await adserver({
    url: `/proposal/${proposalId}/materials/test/${testId}`,
    method: 'PATCH',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response;
};

export const useUpdateMaterialTest = (): UseMutationResult<
  MaterialTestParams,
  unknown,
  { proposalId: number; testId: string | number; data: string; file?: File },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMaterialTest,
    onSuccess: (_response, { proposalId }) =>
      invalidateMaterials(queryClient, proposalId),
  });
};

const rectifyMaterialTest = async ({
  proposalId,
  testId,
  commentary,
}: {
  proposalId: number;
  testId: string | number;
  commentary: string;
}): Promise<MaterialTestParams> => {
  const { data } = await adserver({
    url: `/proposal/${proposalId}/materials/test/${testId}/rectify`,
    method: 'POST',
    data: { commentary },
    headers: {},
  });
  return data;
};

export const useRectifyMaterialTest = (): UseMutationResult<
  MaterialTestParams,
  unknown,
  { proposalId: number; testId: string | number; commentary: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: rectifyMaterialTest,
    onSuccess: (_data, { proposalId }) =>
      invalidateMaterials(queryClient, proposalId),
  });
};

const acceptMaterialTest = async ({
  proposalId,
  testId,
}: {
  proposalId: number;
  testId: string | number;
}): Promise<MaterialTestParams> => {
  const { data } = await adserver({
    url: `/proposal/${proposalId}/materials/test/${testId}/accept`,
    method: 'POST',
    headers: {},
  });
  return data;
};

export const useAcceptMaterialTest = (): UseMutationResult<
  MaterialTestParams,
  unknown,
  { proposalId: number; testId: string | number },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acceptMaterialTest,
    onSuccess: (_data, { proposalId }) =>
      invalidateMaterials(queryClient, proposalId),
  });
};

// accept-all

const acceptAllMaterials = async ({
  proposalId,
}: {
  proposalId: number;
}): Promise<void> => {
  await adserver({
    url: `/proposal/${proposalId}/materials/accept-all`,
    method: 'POST',
    headers: {},
  });
};

export const useAcceptAllMaterials = (): UseMutationResult<
  void,
  unknown,
  { proposalId: number },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acceptAllMaterials,
    onSuccess: (_data, { proposalId }) => {
      queryClient.invalidateQueries({
        queryKey: proposalsKeys.detail(proposalId),
      });
      invalidateMaterials(queryClient, proposalId);
    },
  });
};

// download-all-files
const downloadAllMaterialFiles = async ({
  proposalId,
}: {
  proposalId: number;
}): Promise<void> => {
  await adserver({
    url: `/proposal/${proposalId}/materials/file/download-all`,
    method: 'GET',
    headers: {},
  });
};

export const useDownloadAllMaterialFiles = (): UseMutationResult<
  void,
  unknown,
  { proposalId: number },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: downloadAllMaterialFiles,
    onSuccess: (_data, { proposalId }) => {
      queryClient.invalidateQueries({
        queryKey: proposalsKeys.detail(proposalId),
      });
      invalidateMaterials(queryClient, proposalId);
    },
  });
};
